const API_URL = process.env.REACT_APP_API_URL || '/api';

const NETWORK_ERROR = 'Нет связи с сервером — проверьте интернет и повторите';

class ApiService {
  constructor() {
    this.token = localStorage.getItem('staff_token');
  }

  setToken(token) {
    this.token = token;
    localStorage.setItem('staff_token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('staff_token');
  }

  async request(endpoint, options = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    let response;
    try {
      response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers,
      });
    } catch (e) {
      // Браузер не дождался ответа (нет сети, сервер перезапускается) — вместо «Failed to fetch»
      throw new Error(NETWORK_ERROR);
    }

    if (response.status === 401) {
      this.clearToken();
      window.location.reload();
      throw new Error('Unauthorized');
    }

    // Учётке нужно сменить пароль (вошли по временному на другом устройстве или
    // пароль слабее правил) — перезагрузка покажет экран смены вместо потока ошибок
    if (response.status === 403) {
      const body = await response.clone().json().catch(() => ({}));
      if (body.detail === 'Сначала смените пароль') {
        window.location.reload();
        throw new Error(body.detail);
      }
    }

    // Продление токена — бэкенд выдаёт новый при каждом запросе
    const newToken = response.headers.get('X-New-Token');
    if (newToken) {
      this.setToken(newToken);
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const detail = error.detail;
      const message = Array.isArray(detail)
        ? detail.map(e => e.msg || JSON.stringify(e)).join('; ')
        : (typeof detail === 'string' ? detail : 'Request failed');
      const err = new Error(message);
      err.response = { data: error };
      throw err;
    }

    return response.json();
  }

  // === Auth (unified staff login) ===
  async login(login, password) {
    const response = await fetch(`${API_URL}/auth/staff/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ login, password }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const err = new Error(error.detail || 'Invalid credentials');
      // 429 — три неверные попытки подряд, сервер держит паузу (Retry-After, с)
      if (response.status === 429) {
        err.retryAfter = parseInt(response.headers.get('Retry-After'), 10) || 10;
      }
      throw err;
    }

    const data = await response.json();
    this.setToken(data.access_token);
    return data;
  }

  async checkAuth() {
    return this.request('/auth/staff/me');
  }

  // Смена своего пароля: после входа по временному — без текущего. Ответ — новый
  // токен (сервер поднял версию, прежний токен этой сессии уже недействителен).
  async changePassword(newPassword, currentPassword = null) {
    const data = await this.request('/auth/staff/change-password', {
      method: 'POST',
      body: JSON.stringify({ new_password: newPassword, current_password: currentPassword }),
    });
    this.setToken(data.access_token);
    return data;
  }

  // «Забыли пароль?» — без токена: человек ещё не вошёл
  async _recoveryPost(path, body) {
    const response = await fetch(`${API_URL}/auth/staff/recovery/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(data.detail || 'Не удалось отправить');
      if (response.status === 429) {
        err.retryAfter = parseInt(response.headers.get('Retry-After'), 10) || 60;
      }
      throw err;
    }
    return data;
  }

  async recoveryOptions(login) {
    return this._recoveryPost('options', { login });
  }

  async recoverySend(login, channel) {
    return this._recoveryPost('send', { login, channel });
  }

  // === Products ===
  async getProducts(page = 1, limit = 50, archived = false, date = null, brief = false) {
    let url = `/products/all?page=${page}&limit=${limit}&archived=${archived}`;
    if (date) url += `&date=${date}`;
    if (brief) url += '&brief=1';  // без картинок/future_stock — для панелей, где нужны только цены и остатки
    return this.request(url);
  }

  // Все товары (активные + архивные) одним списком — для поиска в фильтрах.
  async getProductsAll(includeInactive = false) {
    const q = includeInactive ? '?include_inactive=true' : '?limit=500';
    return this.request(`/products/all${q}`);
  }

  // Поле «Цветок» в карточке: список видов для подсказки и вид, угаданный по названию
  async getProductFlowers(name = '') {
    return this.request(`/products/flowers?name=${encodeURIComponent(name || '')}`);
  }

  async createProduct(data) {
    return this.request('/products', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProduct(id, data) {
    return this.request(`/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProduct(id) {
    return this.request(`/products/${id}`, {
      method: 'DELETE',
    });
  }

  async uploadProductImage(productId, file) {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_URL}/products/${productId}/images`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Upload failed');
    }

    return response.json();
  }

  // Фото одного стебля для Flowwow (главное фото карточки на площадке, в каталоге не показывается)
  async uploadProductFlowwowImage(productId, file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_URL}/products/${productId}/flowwow-image`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${this.token}` },
      body: formData,
    });
    if (!response.ok) {
      throw new Error('Upload failed');
    }
    return response.json();
  }

  async deleteProductFlowwowImage(productId) {
    return this.request(`/products/${productId}/flowwow-image`, { method: 'DELETE' });
  }

  async deleteProductImage(productId, imageId) {
    return this.request(`/products/${productId}/images/${imageId}`, {
      method: 'DELETE',
    });
  }

  async rotateProductImage(productId, imageId, direction = 'cw') {
    return this.request(`/products/${productId}/images/${imageId}/rotate?direction=${direction}`, {
      method: 'POST',
    });
  }

  // === Bouquets (admin) ===
  async getBouquetsAdmin(archived = false, kind = null) {
    let url = `/bouquets/all/list?archived=${archived}`;
    if (kind) url += `&kind=${kind}`;
    return this.request(url);
  }

  async getBouquetDetail(id) {
    return this.request(`/bouquets/${id}`);
  }

  async createBouquet(data) {
    return this.request('/bouquets', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateBouquet(id, data) {
    return this.request(`/bouquets/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async archiveBouquet(id) {
    return this.request(`/bouquets/${id}/archive`, { method: 'POST' });
  }

  async restoreBouquet(id) {
    return this.request(`/bouquets/${id}/restore`, { method: 'POST' });
  }

  async deleteBouquet(id) {
    return this.request(`/bouquets/${id}`, { method: 'DELETE' });
  }

  async disassembleBouquet(id, items, reason = null, lot_id = null) {
    return this.request(`/bouquets/${id}/disassemble`, {
      method: 'POST',
      body: JSON.stringify({ items, reason, lot_id }),
    });
  }

  async updateBouquetLot(bouquetId, lotId, data) {
    return this.request(`/bouquets/${bouquetId}/lots/${lotId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async assembleBouquet(id, quantity, shelf_life_days = null, stock_arrival_date = null) {
    return this.request(`/bouquets/${id}/assemble`, {
      method: 'POST',
      body: JSON.stringify({ quantity, shelf_life_days, stock_arrival_date }),
    });
  }

  // === Задания на сборку букетов ===
  async createAssemblyTask(bouquetId, quantity, florist_id = null, shelf_life_days = null, assembled_at = null) {
    return this.request(`/bouquets/${bouquetId}/assembly-task`, {
      method: 'POST',
      body: JSON.stringify({ quantity, florist_id, shelf_life_days, assembled_at }),
    });
  }

  async getMyAssemblyTasks() {
    return this.request('/bouquets/assembly-tasks/my');
  }

  async getBouquetAssemblyTasks(bouquetId) {
    return this.request(`/bouquets/${bouquetId}/assembly-tasks`);
  }

  async completeAssemblyTask(taskId) {
    return this.request(`/bouquets/assembly-tasks/${taskId}/complete`, { method: 'POST' });
  }

  async cancelAssemblyTask(taskId) {
    return this.request(`/bouquets/assembly-tasks/${taskId}/cancel`, { method: 'POST' });
  }

  async uploadBouquetImage(bouquetId, file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_URL}/bouquets/${bouquetId}/images`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${this.token}` },
      body: formData,
    });
    if (!response.ok) throw new Error('Upload failed');
    return response.json();
  }

  async deleteBouquetImage(bouquetId, imageId) {
    return this.request(`/bouquets/${bouquetId}/images/${imageId}`, { method: 'DELETE' });
  }

  // === Categories ===
  async getCategories(scope = null) {
    const q = scope ? `?scope=${scope}` : '';
    return this.request(`/categories/all${q}`);
  }

  async reorderCategories(scope, ordered_ids) {
    return this.request('/categories/reorder', {
      method: 'POST',
      body: JSON.stringify({ scope, ordered_ids }),
    });
  }

  async createCategory(data) {
    return this.request('/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCategory(id, data) {
    return this.request(`/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCategory(id) {
    return this.request(`/categories/${id}`, {
      method: 'DELETE',
    });
  }

  // === Orders (admin) ===
  async getOrders({ status = null, search = '', orderId = null, orderDateFrom = '', orderDateTo = '', deliveryDateFrom = '', deliveryDateTo = '', limit = 100, offset = 0 } = {}) {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (search) params.append('search', search);
    if (orderId) params.append('order_id', orderId);
    if (orderDateFrom) params.append('order_date_from', orderDateFrom);
    if (orderDateTo) params.append('order_date_to', orderDateTo);
    if (deliveryDateFrom) params.append('delivery_date_from', deliveryDateFrom);
    if (deliveryDateTo) params.append('delivery_date_to', deliveryDateTo);
    params.append('limit', limit);
    params.append('offset', offset);
    return this.request(`/orders?${params.toString()}`);
  }

  async getOrdersStats() {
    return this.request('/orders/stats');
  }

  async getOrdersDashboardStats() {
    return this.request('/orders/stats/dashboard');
  }

  async getOrderById(id) {
    return this.request(`/orders/${id}`);
  }

  async getStatsSummary({ dateFrom, dateTo, periodDays } = {}) {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (periodDays) params.append('period_days', periodDays);
    const query = params.toString();
    return this.request(`/orders/stats/summary${query ? '?' + query : ''}`);
  }

  async getDailyStats({ dateFrom, dateTo, periodDays } = {}) {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (periodDays) params.append('period_days', periodDays);
    const query = params.toString();
    return this.request(`/orders/stats/daily${query ? '?' + query : ''}`);
  }

  async getProductStats({ dateFrom, dateTo, periodDays } = {}) {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (periodDays) params.append('period_days', periodDays);
    const query = params.toString();
    return this.request(`/orders/stats/products${query ? '?' + query : ''}`);
  }

  async getCourierStats({ dateFrom, dateTo, periodDays } = {}) {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (periodDays) params.append('period_days', periodDays);
    const query = params.toString();
    return this.request(`/orders/stats/couriers${query ? '?' + query : ''}`);
  }

  async getProcurement(dateFrom, dateTo) {
    return this.request(`/orders/procurement?date_from=${dateFrom}&date_to=${dateTo}`);
  }

  async getOrdersByDate(date) {
    return this.request(`/orders/by-date/${date}`);
  }

  async getOrderCountsByDate(dateFrom, dateTo) {
    return this.request(`/orders/counts-by-date?date_from=${dateFrom}&date_to=${dateTo}`);
  }

  async updateOrder(id, data) {
    return this.request(`/orders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async createAdminOrder(data) {
    return this.request('/orders/admin', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async editOrder(id, data) {
    return this.request(`/orders/${id}/edit`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async previewEditOrder(id, data) {
    return this.request(`/orders/${id}/preview-edit`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getOrderDateAvailability(orderId, days = 60) {
    return this.request('/orders/date-availability', {
      method: 'POST',
      body: JSON.stringify({ order_id: orderId, days }),
    });
  }

  async getAvailableMaxQtyBatch(productIds, deliveryDate, orderId = null) {
    return this.request('/orders/available-max-qty-batch', {
      method: 'POST',
      body: JSON.stringify({ product_ids: productIds, delivery_date: deliveryDate, order_id: orderId }),
    });
  }

  async getBouquetsAvailableMaxQtyBatch(bouquetIds, deliveryDate, excludeOrderId = null, cartItemsPending = []) {
    return this.request('/bouquets/available-max-qty-batch', {
      method: 'POST',
      body: JSON.stringify({
        bouquet_ids: bouquetIds,
        date: deliveryDate,
        exclude_order_id: excludeOrderId,
        cart_items_pending: cartItemsPending,
      }),
    });
  }

  async resolveAdjustment(orderId, adjustmentId, data) {
    return this.request(`/orders/${orderId}/adjustments/${adjustmentId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async calculateDelivery(address, lat, lon) {
    const params = new URLSearchParams();
    if (address) params.append('address', address);
    if (lat != null) params.append('lat', lat);
    if (lon != null) params.append('lon', lon);
    return this.request(`/delivery/calculate?${params.toString()}`);
  }

  async notifyOrderStatus(orderId, status = null) {
    const url = status
      ? `/notifications/order-status/${orderId}?status=${status}`
      : `/notifications/order-status/${orderId}`;
    return this.request(url, {
      method: 'POST',
    });
  }

  // === Florist orders ===
  async getMyOrdersByDate(date) {
    return this.request(`/orders/my/by-date/${date}`);
  }

  async updateMyOrder(orderId, data) {
    return this.request(`/orders/my/${orderId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // === Packer orders ===
  async getPackerOrdersByDate(date) {
    return this.request(`/orders/packer/by-date/${date}`);
  }

  async updatePackerOrder(orderId, data) {
    return this.request(`/orders/packer/${orderId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // === Courier orders ===
  async getCourierOrders(date = null) {
    const params = date ? `?date=${date}` : '';
    return this.request(`/orders/courier/my${params}`);
  }

  async updateCourierOrder(orderId, data) {
    return this.request(`/orders/courier/my/${orderId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // === Users ===
  async getUsersStats() {
    return this.request('/users/stats');
  }

  // filters (27.10): joined_from, joined_to, platform, source, bought, idle_days, reach, segment
  async getUsers({ searchUsername = '', searchTelegramId = '', searchMaxId = '', searchPlatformId = '', searchPhone = '', sortBy = '', sortOrder = 'desc', limit = 100, offset = 0, filters = {} } = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v != null) params.append(k, v); });
    if (searchUsername) params.append('search_username', searchUsername);
    if (searchTelegramId) params.append('search_telegram_id', searchTelegramId);
    if (searchMaxId) params.append('search_max_id', searchMaxId);
    if (searchPlatformId) params.append('search_platform_id', searchPlatformId);
    if (searchPhone) params.append('search_phone', searchPhone);
    if (sortBy) params.append('sort_by', sortBy);
    if (sortOrder) params.append('sort_order', sortOrder);
    params.append('limit', limit);
    params.append('offset', offset);
    return this.request(`/users?${params.toString()}`);
  }

  // «Откуда пришёл» — варианты для фильтра списка «Клиенты»
  async getUserSources() {
    return this.request('/users/sources');
  }

  async getUserBonusHistory(userId) {
    return this.request(`/users/by-id/${userId}/bonus-history`);
  }

  async getUserById(userId) {
    return this.request(`/users/by-id/${userId}`);
  }

  async getUserOrders(userId, limit = 50, offset = 0) {
    const params = new URLSearchParams();
    params.append('limit', limit);
    params.append('offset', offset);
    return this.request(`/users/by-id/${userId}/orders?${params.toString()}`);
  }

  async addUserBonus(userId, amount, comment) {
    return this.request(`/users/by-id/${userId}/bonus/add`, {
      method: 'POST',
      body: JSON.stringify({ amount, comment }),
    });
  }

  async subtractUserBonus(userId, amount, comment) {
    return this.request(`/users/by-id/${userId}/bonus/subtract`, {
      method: 'POST',
      body: JSON.stringify({ amount, comment }),
    });
  }

  // Account merge/unmerge
  async mergeUsers(sourceId, targetId) {
    return this.request('/users/admin/merge', {
      method: 'POST',
      body: JSON.stringify({ source_id: sourceId, target_id: targetId }),
    });
  }

  async unmergeUser(userId, platformToSplit, bonusRecipient = 'main') {
    return this.request('/users/admin/unmerge', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        platform_to_split: platformToSplit,
        bonus_recipient: bonusRecipient,
      }),
    });
  }

  async getUnmergePreview(userId, platform) {
    const params = new URLSearchParams({ user_id: userId, platform });
    return this.request(`/users/admin/unmerge-preview?${params.toString()}`);
  }

  async updateUserBonus(userId, bonusBalance) {
    return this.request(`/users/by-id/${userId}/bonus`, {
      method: 'PUT',
      body: JSON.stringify({ bonus_balance: bonusBalance }),
    });
  }

  // === Broadcast ===
  async sendBroadcast({ textTg, textMax, platform, imageFiles = [], kind = 'mass', singleTgId = '', singleMaxId = '', pacePerMin = '' }) {
    const formData = new FormData();
    if (textTg) formData.append('text_tg', textTg);
    if (textMax) formData.append('text_max', textMax);
    formData.append('platform', platform);
    formData.append('kind', kind);
    if (singleTgId) formData.append('single_tg_id', singleTgId);
    if (singleMaxId) formData.append('single_max_id', singleMaxId);
    if (pacePerMin !== '') formData.append('pace_per_min', String(pacePerMin));
    imageFiles.forEach((f) => formData.append('images', f));

    let response;
    try {
      response = await fetch(`${API_URL}/notifications/broadcast`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`,
        },
        body: formData,
      });
    } catch (e) {
      const err = new Error(NETWORK_ERROR);
      err.network = true;   // страница сверит с реестром: создалась рассылка или нет
      throw err;
    }
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Ошибка рассылки');
    }
    return response.json();
  }

  async getBroadcastStatus(broadcastId) {
    return this.request(`/notifications/broadcast/${broadcastId}`);
  }

  async getBroadcasts(kind = 'all', limit = 30, offset = 0) {
    return this.request(`/notifications/broadcast/list?kind=${kind}&limit=${limit}&offset=${offset}`);
  }

  async cancelBroadcast(broadcastId) {
    return this.request(`/notifications/broadcast/${broadcastId}/cancel`, { method: 'POST' });
  }

  async pauseBroadcast(broadcastId) {
    return this.request(`/notifications/broadcast/${broadcastId}/pause`, { method: 'POST' });
  }

  async resumeBroadcast(broadcastId) {
    return this.request(`/notifications/broadcast/${broadcastId}/resume`, { method: 'POST' });
  }

  // Скачивание лога рассылки (JSONL / CSV с ';') — с токеном, через blob
  async downloadBroadcastLog(broadcastId, format = 'jsonl') {
    const response = await fetch(`${API_URL}/notifications/broadcast/${broadcastId}/log?format=${format}`, {
      headers: { 'Authorization': `Bearer ${this.token}` },
    });
    if (!response.ok) throw new Error('Лог не найден');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `broadcast-${broadcastId}.${format === 'csv' ? 'csv' : 'jsonl'}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async getUsersCount(platform) {
    const params = new URLSearchParams();
    if (platform === 'telegram') params.append('search_telegram_id', '%');
    if (platform === 'max') params.append('search_telegram_id', '%');
    params.append('limit', '0');
    params.append('offset', '0');
    return this.request(`/users?${params.toString()}`);
  }

  // === Available dates ===
  async getAvailableDates(items, days = 30) {
    return this.request('/orders/available-dates', {
      method: 'POST',
      body: JSON.stringify({ items, days }),
    });
  }

  // === Packaging ===
  async getPackagingInventory(includeHidden = false) {
    return this.request(`/packaging${includeHidden ? '?include_hidden=true' : ''}`);
  }

  async createPackaging(data) {
    return this.request('/packaging', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePackaging(type, data) {
    return this.request(`/packaging/${type}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePackaging(type) {
    return this.request(`/packaging/${type}`, {
      method: 'DELETE',
    });
  }

  async uploadPackagingImage(type, file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_URL}/packaging/${type}/image`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${this.token}` },
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Upload failed');
    }
    return response.json();
  }

  async deletePackagingImage(type) {
    return this.request(`/packaging/${type}/image`, { method: 'DELETE' });
  }

  // === Settings ===
  async getSettings() {
    return this.request('/settings');
  }

  async updateOrderLeadTime(minutes) {
    return this.request(`/settings/order-lead-time?minutes=${minutes}`, {
      method: 'PUT',
    });
  }

  async updateServiceFee(percent) {
    return this.request(`/settings/service-fee?percent=${percent}`, {
      method: 'PUT',
    });
  }

  async updateWalkinServiceFee(bouquetPercent) {
    return this.request(`/settings/walkin-service-fee?bouquet_percent=${bouquetPercent}`, {
      method: 'PUT',
    });
  }

  async updateCatalogHorizon(days) {
    return this.request(`/settings/catalog-horizon?days=${days}`, {
      method: 'PUT',
    });
  }

  async updateReviewLinks({ review_2gis_url, review_yandex_url }) {
    return this.request('/settings/review-links', {
      method: 'PUT',
      body: JSON.stringify({ review_2gis_url, review_yandex_url }),
    });
  }

  async updateLegalLinks({ oferta_url, privacy_policy_url, privacy_policy_version }) {
    return this.request('/settings/legal-links', {
      method: 'PUT',
      body: JSON.stringify({ oferta_url, privacy_policy_url, privacy_policy_version }),
    });
  }

  async updateCatalogLabels(labels) {
    // {catalog_label_products?, catalog_label_bouquets?, catalog_label_subscription?} — только owner
    return this.request('/settings/catalog-labels', {
      method: 'PUT',
      body: JSON.stringify(labels),
    });
  }

  async updateBonusPercent(percent) {
    return this.request(`/settings/bonus-percent?percent=${percent}`, {
      method: 'PUT',
    });
  }

  async updateMinOrderAmount(amount) {
    return this.request(`/settings/min-order-amount?amount=${amount}`, {
      method: 'PUT',
    });
  }

  async updatePostcardPrice(price) {
    return this.request(`/settings/postcard-price?price=${price}`, {
      method: 'PUT',
    });
  }

  async updateMaxDeliveryDistance(distance_km) {
    return this.request(`/settings/max-delivery-distance?distance_km=${distance_km}`, {
      method: 'PUT',
    });
  }

  async updateMaxDisplayQuantity(quantity) {
    const qs = quantity ? `?quantity=${quantity}` : '';
    return this.request(`/settings/max-display-quantity${qs}`, {
      method: 'PUT',
    });
  }

  async updateVaseMinDeliveries(count) {
    return this.request(`/settings/vase-min-deliveries?count=${count}`, {
      method: 'PUT',
    });
  }

  async updateSubscriptionDelivery(mode) {
    return this.request(`/settings/subscription-delivery?mode=${encodeURIComponent(mode)}`, {
      method: 'PUT',
    });
  }

  async getCourierCommission() {
    return this.request('/settings/courier-commission');
  }

  async updateCourierCommission(percent) {
    return this.request(`/settings/courier-commission?percent=${percent}`, {
      method: 'PUT',
    });
  }

  // Blocked Slots
  async getBlockedSlots() {
    return this.request('/settings/blocked-slots');
  }

  async createBlockedSlots(data) {
    return this.request('/settings/blocked-slots', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteBlockedSlot(id) {
    return this.request(`/settings/blocked-slots/${id}`, {
      method: 'DELETE',
    });
  }

  async getBlockedSlotsForDate(date) {
    return this.request(`/settings/blocked-slots/check?date=${date}`);
  }

  async getFullyBlockedDates() {
    return this.request('/settings/blocked-slots/fully-blocked');
  }

  // === Pickup Points ===
  async getPickupPoints() {
    return this.request('/settings/pickup-points/all');
  }

  async createPickupPoint(data) {
    return this.request('/settings/pickup-points', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePickupPoint(id, data) {
    return this.request(`/settings/pickup-points/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePickupPoint(id) {
    return this.request(`/settings/pickup-points/${id}`, {
      method: 'DELETE',
    });
  }

  // === Delivery Tariffs ===
  async getDeliveryTariffs() {
    return this.request('/settings/delivery-tariffs');
  }

  async updateDeliveryTariffs(tariffs) {
    return this.request('/settings/delivery-tariffs', {
      method: 'PUT',
      body: JSON.stringify(tariffs)
    });
  }

  async geolocateAddress(lat, lon) {
    return this.request(`/address/geolocate?lat=${lat}&lon=${lon}`);
  }

  // Новый формат тарифов: произвольные интервалы «до X км — цена Y»
  async getDeliveryTariffIntervals() {
    return this.request('/settings/delivery-tariff-intervals');
  }

  async updateDeliveryTariffIntervals(payload) {
    return this.request('/settings/delivery-tariff-intervals', {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
  }

  // === Employees ===
  async getEmployees(role = null) {
    const params = role ? `?role=${role}` : '';
    return this.request(`/employees${params}`);
  }

  async getAllEmployees(role = null) {
    const params = role ? `?role=${role}` : '';
    return this.request(`/employees/all${params}`);
  }

  async createEmployee(data) {
    return this.request('/employees', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateEmployee(id, data) {
    return this.request(`/employees/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async resetEmployeePassword(id) {
    return this.request(`/employees/${id}/reset-password`, { method: 'POST' });
  }

  async lookupEmployeeClient(telegramId, maxId) {
    const q = new URLSearchParams();
    if (telegramId) q.set('telegram_id', telegramId);
    if (maxId) q.set('max_id', maxId);
    return this.request(`/employees/client-lookup?${q}`);
  }

  async deleteEmployee(id) {
    return this.request(`/employees/${id}`, {
      method: 'DELETE',
    });
  }

  // === Packer Workflow ===
  async getPhotoControlEnabled() {
    return this.request('/settings/photo-control');
  }

  async setPhotoControlEnabled(enabled, retention_days) {
    const body = {};
    if (enabled !== undefined) body.enabled = enabled;
    if (retention_days !== undefined) body.retention_days = retention_days;
    return this.request('/settings/photo-control', { method: 'PUT', body: JSON.stringify(body) });
  }

  async addOrderPhoto(orderId, file) {
    const formData = new FormData();
    formData.append('photo', file);
    const response = await fetch(`${API_URL}/orders/${orderId}/photos`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${this.token}` },
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Не удалось загрузить фото');
    }
    return response.json();
  }

  async deleteOrderPhoto(orderId, photoId) {
    return this.request(`/orders/${orderId}/photos/${photoId}`, { method: 'DELETE' });
  }

  async flowwowResendPhoto(orderId) {
    return this.request(`/flowwow/orders/${orderId}/photo-before/resend`, { method: 'POST' });
  }

  // === Статусы заказа: тексты уведомлений покупателю под клиента ===
  async getOrderStatusesConfig() {
    return this.request('/settings/order-statuses');
  }

  async updateOrderStatusesConfig({ messages } = {}) {
    // Тексты писем покупателю; пустая строка или дефолт снимает переопределение
    return this.request('/settings/order-statuses', {
      method: 'PUT',
      body: JSON.stringify({ messages }),
    });
  }

  async getPackerWorkflowEnabled() {
    return this.request('/settings/packer-workflow');
  }

  async setPackerWorkflowEnabled(enabled) {
    return this.request('/settings/packer-workflow', {
      method: 'PUT',
      body: JSON.stringify({ enabled })
    });
  }

  // === Workload ===
  async getWorkloadStats(date) {
    const params = date ? `?date=${date}` : '';
    return this.request(`/workload/stats${params}`);
  }

  // === Courier Assignment ===
  async getClusters(date, timeSlot = null, maxDurationMinutes = 15, maxClusterSize = 7, numClusters = null, balanced = false) {
    const body = {
      date,
      time_slot: timeSlot,
      max_duration_minutes: maxDurationMinutes,
      max_cluster_size: maxClusterSize,
      balanced,
    };

    // Добавляем num_clusters только если указано
    if (numClusters !== null && numClusters !== undefined && numClusters !== '') {
      body.num_clusters = parseInt(numClusters);
    }

    return this.request('/courier-assignment/clusters', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async assignCouriers(assignments) {
    return this.request('/courier-assignment/assign', {
      method: 'POST',
      body: JSON.stringify({ assignments }),
    });
  }

  async updateOrderStatusFromAssignment(orderId, status) {
    return this.request(`/courier-assignment/orders/${orderId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  }

  // persist=true — записать порядок в delivery_sequence (только назначенная группа одного курьера)
  async reoptimizeCluster(orderIds, persist = false) {
    return this.request('/courier-assignment/reoptimize-cluster', {
      method: 'POST',
      body: JSON.stringify({ order_ids: orderIds, persist }),
    });
  }

  // Маршруты назначенных групп (геометрия + ETA) для карты
  async getAssignedRoutes(date, timeSlot = null) {
    const params = new URLSearchParams({ date });
    if (timeSlot) params.append('time_slot', timeSlot);
    return this.request(`/courier-assignment/assigned-routes?${params.toString()}`);
  }

  // Ручной порядок объезда назначенной группы: 1..N по позиции в orderIds
  async setAssignedSequence(courierId, date, orderIds) {
    return this.request(`/courier-assignment/assigned-groups/${courierId}/sequence`, {
      method: 'PUT',
      body: JSON.stringify({ date, order_ids: orderIds }),
    });
  }

  // === Геолокация курьеров ===
  async getMyLocationState() {
    return this.request('/courier-assignment/me/location');
  }

  async setLocationConsent() {
    return this.request('/courier-assignment/me/location/consent', { method: 'POST' });
  }

  async postMyLocation(data) {
    return this.request('/courier-assignment/me/location', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteMyLocation() {
    return this.request('/courier-assignment/me/location', { method: 'DELETE' });
  }

  async getCourierLocations() {
    return this.request('/courier-assignment/locations');
  }

  async getOrdersStreamToken() {
    return this.request('/orders/stream-token', { method: 'POST' });
  }

  async getLocationsStreamToken() {
    return this.request('/courier-assignment/locations/stream-token', { method: 'POST' });
  }

  async getOrdersBySlot(date, timeSlot = null) {
    const params = new URLSearchParams({ date });
    if (timeSlot) params.append('time_slot', timeSlot);
    return this.request(`/courier-assignment/orders-by-slot?${params.toString()}`);
  }

  // Только цифры для чипов слотов — без полных карточек заказов
  async getSlotCounts(date) {
    return this.request(`/courier-assignment/slot-counts?date=${encodeURIComponent(date)}`);
  }

  // === Analytics ===
  async getAnalyticsFunnel({ dateFrom, dateTo, uniqueDaily } = {}) {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (uniqueDaily) params.append('unique_daily', 'true');
    const query = params.toString();
    return this.request(`/analytics/funnel${query ? '?' + query : ''}`);
  }

  // Аналитика v3 (12.10.2026): экраны по подключённым модулям, только владелец.
  // screen: overview | channels | products | customers; q: { period, from, to, basis }
  async getAnalyticsLayout() {
    return this.request('/analytics/v3/layout');
  }

  async getAnalyticsScreen(screen, q = {}) {
    const params = new URLSearchParams();
    if (q.from || q.to) {
      if (q.from) params.append('from', q.from);
      if (q.to) params.append('to', q.to);
    } else if (q.period) {
      params.append('period', q.period);
    }
    if (q.basis) params.append('basis', q.basis);
    if (q.compare) params.append('compare', q.compare);
    return this.request(`/analytics/v3/${screen}?${params.toString()}`);
  }

  async getAnalyticsSettings() {
    return this.request('/analytics/v3/settings');
  }

  async saveAnalyticsSettings(data) {
    return this.request('/analytics/v3/settings', { method: 'PUT', body: JSON.stringify(data) });
  }

  // Сводка за неделю владельцам (по понедельникам)
  async getWeeklySummary() {
    return this.request('/analytics/v3/weekly-summary');
  }

  async setWeeklySummaryOwner(employeeId, enabled) {
    return this.request(`/analytics/v3/weekly-summary/${employeeId}`, { method: 'PUT', body: JSON.stringify({ enabled }) });
  }

  async sendWeeklySummaryToMe() {
    return this.request('/analytics/v3/weekly-summary/send-me', { method: 'POST' });
  }

  // --- Источники заказов (платный модуль): ссылки и QR по местам, памятка ---
  async getMarketingLinks() {
    return this.request('/marketing/links');
  }

  async createMarketingLink(data) {
    return this.request('/marketing/links', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateMarketingLink(id, data) {
    return this.request(`/marketing/links/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteMarketingLink(id) {
    return this.request(`/marketing/links/${id}`, { method: 'DELETE' });
  }

  // QR-код картинкой: с токеном, поэтому через fetch → blob → адрес для <img>
  async getMarketingQrUrl(id, entry = 'site', size = 600) {
    const response = await fetch(`${API_URL}/marketing/links/${id}/qr?entry=${entry}&size=${size}`, {
      headers: { 'Authorization': `Bearer ${this.token}` },
    });
    if (!response.ok) throw new Error('QR-код не получился');
    return URL.createObjectURL(await response.blob());
  }

  async getMarketingShare() {
    return this.request('/marketing/share');
  }

  async createMarketingShare() {
    return this.request('/marketing/share', { method: 'POST' });
  }

  async disableMarketingShare() {
    return this.request('/marketing/share', { method: 'DELETE' });
  }

  // Расходы на продвижение: ручные по строкам отчёта и расход Директа по кампаниям
  async createMarketingExpense(data) {
    return this.request('/marketing/expenses', { method: 'POST', body: JSON.stringify(data) });
  }

  async updateMarketingExpense(id, data) {
    return this.request(`/marketing/expenses/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteMarketingExpense(id) {
    return this.request(`/marketing/expenses/${id}`, { method: 'DELETE' });
  }

  async setDirectCampaignTarget(campaignId, targetKey) {
    return this.request(`/marketing/direct/campaigns/${campaignId}`, { method: 'PUT', body: JSON.stringify({ target_key: targetKey || null }) });
  }

  // Какая ссылка стоит в объявлении Директа в Telegram / Max (27.10); номер объявления — строкой
  async setDirectAdTarget(adId, targetKey) {
    return this.request(`/marketing/direct/ads/${adId}`, { method: 'PUT', body: JSON.stringify({ target_key: targetKey || null }) });
  }

  async syncDirectSpend() {
    return this.request('/marketing/direct/sync', { method: 'POST' });
  }

  // --- Затраты магазина («Финансы → Затраты», 13.10): аренда, зарплата, продвижение — план и факт
  // по месяцам. Все методы возвращают экран месяца (?month=) — его и показываем
  async getExpenseMonth(month) {
    return this.request(`/expenses/months/${month}`);
  }

  // «Перенести из прошлого месяца»: кандидаты и перенос (возвращает экран месяца)
  async getExpenseCarry(month) {
    return this.request(`/expenses/months/${month}/carry`);
  }

  async carryExpenses(month, items) {
    return this.request(`/expenses/months/${month}/carry`, { method: 'POST', body: JSON.stringify({ items }) });
  }

  async createExpense(month, data) {
    return this.request(`/expenses?month=${month}`, { method: 'POST', body: JSON.stringify(data) });
  }

  async updateExpense(id, month, data) {
    return this.request(`/expenses/${id}?month=${month}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteExpense(id, month) {
    return this.request(`/expenses/${id}?month=${month}`, { method: 'DELETE' });
  }

  // Факт за месяц: {fact: N} — своя сумма, {as_plan: true} — как в плане, {fact: null} — вернуть план
  async setExpenseFact(id, month, body) {
    return this.request(`/expenses/${id}/months/${month}`, { method: 'PUT', body: JSON.stringify(body) });
  }

  async confirmExpenseMonth(month) {
    return this.request(`/expenses/months/${month}/confirm`, { method: 'POST' });
  }

  async closeExpenseMonth(month) {
    return this.request(`/expenses/months/${month}/close`, { method: 'POST' });
  }

  async reopenExpenseMonth(month) {
    return this.request(`/expenses/months/${month}/reopen`, { method: 'POST' });
  }

  async getAnalyticsFunnelDetails({ step, dateFrom, dateTo, uniqueDaily, offset = 0, limit = 100 }) {
    const params = new URLSearchParams({ step });
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (uniqueDaily) params.append('unique_daily', 'true');
    if (offset) params.append('offset', offset.toString());
    if (limit !== 100) params.append('limit', limit.toString());
    const query = params.toString();
    return this.request(`/analytics/funnel/details?${query}`);
  }
  // === Warehouse: Suppliers ===
  async getSuppliers(all = false) {
    return this.request(`/warehouse/suppliers${all ? '?all=true' : ''}`);
  }

  async createSupplier(data) {
    return this.request('/warehouse/suppliers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSupplier(id, data) {
    return this.request(`/warehouse/suppliers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSupplier(id) {
    return this.request(`/warehouse/suppliers/${id}`, {
      method: 'DELETE',
    });
  }

  // === Warehouse: Transfer points (куда перемещаем товар) ===
  async getTransferPoints(all = false) {
    return this.request(`/warehouse/transfer-points${all ? '?all=true' : ''}`);
  }

  async createTransferPoint(data) {
    return this.request('/warehouse/transfer-points', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTransferPoint(id, data) {
    return this.request(`/warehouse/transfer-points/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTransferPoint(id) {
    return this.request(`/warehouse/transfer-points/${id}`, {
      method: 'DELETE',
    });
  }

  // === Warehouse: Invoices ===
  // Списки склада отдаются страницами: { items, total, has_more } (как /users).
  // Все фильтры накладных — серверные (номер, тип, мультивыбор поставщика, дата поступления).
  async getInvoices({ dateFrom, dateTo, supplierId, supplierIds, status, invoiceNumber, invoiceType, arrivalFrom, arrivalTo, limit, offset } = {}) {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (supplierId) params.append('supplier_id', supplierId);
    if (supplierIds && supplierIds.length) params.append('supplier_ids', supplierIds.join(','));
    if (status) params.append('status', status);
    if (invoiceNumber) params.append('invoice_number', invoiceNumber);
    if (invoiceType) params.append('invoice_type', invoiceType);
    if (arrivalFrom) params.append('arrival_from', arrivalFrom);
    if (arrivalTo) params.append('arrival_to', arrivalTo);
    if (limit != null) params.append('limit', limit);
    if (offset != null) params.append('offset', offset);
    const query = params.toString();
    return this.request(`/warehouse/invoices${query ? '?' + query : ''}`);
  }

  async getInvoice(id) {
    return this.request(`/warehouse/invoices/${id}`);
  }

  async createInvoice(data) {
    return this.request('/warehouse/invoices', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateInvoice(id, data) {
    return this.request(`/warehouse/invoices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async acceptInvoice(id, body) {
    const options = { method: 'POST' };
    if (body) {
      options.body = JSON.stringify(body);
    }
    return this.request(`/warehouse/invoices/${id}/accept`, options);
  }

  async deleteInvoice(id) {
    return this.request(`/warehouse/invoices/${id}`, {
      method: 'DELETE',
    });
  }

  // === Warehouse: закупка с биржи (накладные «из биржи», PROCUREMENT_IMPORT_ENABLED) ===
  async getProcurementLines(invoiceId) {
    return this.request(`/warehouse/import/invoices/${invoiceId}/lines`);
  }

  async setProcurementLineProduct(lineId, productId) {
    return this.request(`/warehouse/import/lines/${lineId}/product`, {
      method: 'PATCH',
      body: JSON.stringify({ product_id: productId }),
    });
  }

  // body: { price_cur } — цена биржи за стебель (€/$), ₽ под ключ считает площадка; { price_rub } — если площадка не отвечает
  async setProcurementLinePrice(lineId, body) {
    return this.request(`/warehouse/import/lines/${lineId}/price`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async createProcurementLineProduct(lineId) {
    return this.request(`/warehouse/import/lines/${lineId}/create-product`, { method: 'POST' });
  }

  async getProcurementStatus() {
    return this.request('/warehouse/import/status');
  }

  // «Разбить по цветам» до приёмки: [{ color, product_id?, quantity }]; [] — «Убрать разбивку»
  async setProcurementLineSplits(lineId, splits) {
    return this.request(`/warehouse/import/lines/${lineId}/splits`, {
      method: 'PUT',
      body: JSON.stringify({ splits }),
    });
  }

  async setProcurementExpectedDate(invoiceId, expectedDate) {
    return this.request(`/warehouse/import/invoices/${invoiceId}/expected-date`, {
      method: 'PATCH',
      body: JSON.stringify({ expected_date: expectedDate }),
    });
  }

  // «Принять на склад» накладной из биржи (все строки приняты, у всех товар и цена)
  // versions — { id строки: version } из окна: пока смотрели, строки изменили → 409 «…обновили данные».
  // extra — { invoice_number, date, comment } шапки приёмки
  async acceptProcurementInvoice(invoiceId, versions, extra = {}) {
    return this.request(`/warehouse/import/invoices/${invoiceId}/accept`, {
      method: 'POST',
      body: JSON.stringify({ versions: versions || null, ...extra }),
    });
  }

  // «Сохранить» в форме накладной из биржи: { rows, manual_rows, removed_line_ids, expected_date, … }
  async saveImportForm(invoiceId, body) {
    return this.request(`/warehouse/import/invoices/${invoiceId}/form`, { method: 'PUT', body: JSON.stringify(body) });
  }

  // «+ Лишнее» при приёмке: товар, которого не заказывали
  async addReceivingExtra(invoiceId, productId, quantity) {
    return this.request(`/warehouse/receiving/invoices/${invoiceId}/extra`, {
      method: 'POST',
      body: JSON.stringify({ product_id: productId, quantity }),
    });
  }

  async deleteReceivingLine(lineId) {
    return this.request(`/warehouse/receiving/lines/${lineId}`, { method: 'DELETE' });
  }

  // Строку открыли — у остальных «✎ <имя>» (2 минуты)
  async openReceivingLine(lineId) {
    return this.request(`/warehouse/receiving/lines/${lineId}/open`, { method: 'POST' });
  }

  // Перед «Брак после приёмки»: каким заказам не хватит (как предпросмотр списания)
  async previewProcurementHiddenDefect(lineId, quantity, splitId) {
    const q = new URLSearchParams({ quantity: String(quantity) });
    if (splitId) q.set('split_id', String(splitId));
    return this.request(`/warehouse/import/lines/${lineId}/hidden-defect/preview?${q.toString()}`);
  }

  // multipart: без Content-Type — границу формы ставит браузер
  async _formRequest(endpoint, formData) {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${this.token}` },
      body: formData,
    });
    const newToken = response.headers.get('X-New-Token');
    if (newToken) this.setToken(newToken);
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(typeof err.detail === 'string' ? err.detail : 'Не удалось сохранить');
    }
    return response.json();
  }

  // «Брак после приёмки»: { quantity, reason_code, note, split_id? } + фото
  async addProcurementHiddenDefect(lineId, { quantity, reasonCode, note, splitId }, files) {
    const fd = new FormData();
    fd.append('quantity', String(quantity));
    fd.append('reason_code', String(reasonCode));
    fd.append('note', note || '');
    if (splitId) fd.append('split_id', String(splitId));
    (files || []).forEach(f => fd.append('photos', f));
    return this._formRequest(`/warehouse/import/lines/${lineId}/hidden-defect`, fd);
  }

  async getProcurementClaims(invoiceId) {
    return this.request(`/warehouse/import/invoices/${invoiceId}/claims`);
  }

  async getProcurementClaimsSummary() {
    return this.request('/warehouse/import/claims/summary');
  }

  async decideProcurementClaim(claimId, action) {   // action: 'approve' | 'cancel' | 'retry'
    return this.request(`/warehouse/import/claims/${claimId}/${action}`, { method: 'POST' });
  }

  async replyProcurementClaim(claimId, text, files) {
    const fd = new FormData();
    fd.append('text', text || '');
    (files || []).forEach(f => fd.append('photos', f));
    return this._formRequest(`/warehouse/import/claims/${claimId}/messages`, fd);
  }

  // === Приёмка по строкам (флорист, админ, владелец) ===
  async getReceivingInvoices() {
    return this.request('/warehouse/receiving/invoices');
  }

  async getReceivingInvoice(invoiceId) {
    return this.request(`/warehouse/receiving/invoices/${invoiceId}`);
  }

  // 409 — строку изменил другой сотрудник: err.response.data.line — свежая
  async saveReceivingLine(lineId, body) {
    return this.request(`/warehouse/receiving/lines/${lineId}`, { method: 'PUT', body: JSON.stringify(body) });
  }

  async saveReceivingVase(lineId, version, ratingVase) {
    return this.request(`/warehouse/receiving/lines/${lineId}/vase`, {
      method: 'PUT',
      body: JSON.stringify({ version, rating_vase: ratingVase }),
    });
  }

  async addReceivingPhoto(lineId, file) {
    const fd = new FormData();
    fd.append('photo', file);
    return this._formRequest(`/warehouse/receiving/lines/${lineId}/photos`, fd);
  }

  async deleteReceivingPhoto(lineId, photoId) {
    return this.request(`/warehouse/receiving/lines/${lineId}/photos/${photoId}`, { method: 'DELETE' });
  }

  // Лупа строки и цвета разбивки: { similar, products } — флористу /products/all закрыт
  async getReceivingProductOptions(lineId, color) {
    const q = color ? `?color=${encodeURIComponent(color)}` : '';
    return this.request(`/warehouse/receiving/lines/${lineId}/product-options${q}`);
  }

  // === Warehouse: Write-offs ===
  async getWriteOffs({ dateFrom, dateTo, limit, offset } = {}) {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (limit != null) params.append('limit', limit);
    if (offset != null) params.append('offset', offset);
    const query = params.toString();
    return this.request(`/warehouse/write-offs${query ? '?' + query : ''}`);
  }

  async getWriteOff(id) {
    return this.request(`/warehouse/write-offs/${id}`);
  }

  async previewWriteOff(data) {
    return this.request('/warehouse/write-offs?dry_run=true', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createWriteOff(data) {
    return this.request('/warehouse/write-offs', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // === Warehouse: Misgrades ===
  async getMisgrades({ dateFrom, dateTo, limit, offset } = {}) {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (limit != null) params.append('limit', limit);
    if (offset != null) params.append('offset', offset);
    const query = params.toString();
    return this.request(`/warehouse/misgrades${query ? '?' + query : ''}`);
  }

  async getMisgrade(id) {
    return this.request(`/warehouse/misgrades/${id}`);
  }

  // Себестоимость для пересорта — последняя закупочная цена исходного товара
  async getMisgradeCost(productId) {
    return this.request(`/warehouse/misgrades/cost?product_id=${productId}`);
  }

  async createMisgrade(data) {
    return this.request('/warehouse/misgrades', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // === Warehouse: Inventories ===
  async getInventories({ dateFrom, dateTo, limit, offset } = {}) {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (limit != null) params.append('limit', limit);
    if (offset != null) params.append('offset', offset);
    const query = params.toString();
    return this.request(`/warehouse/inventories${query ? '?' + query : ''}`);
  }

  // Единый реестр инвентаризаций (стебли + букеты) одной страницей, у строк есть kind
  async getInventoryRegistry({ dateFrom, dateTo, limit, offset } = {}) {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (limit != null) params.append('limit', limit);
    if (offset != null) params.append('offset', offset);
    const query = params.toString();
    return this.request(`/warehouse/inventories/registry${query ? '?' + query : ''}`);
  }

  async getInventory(id) {
    return this.request(`/warehouse/inventories/${id}`);
  }

  async createInventory(data) {
    return this.request('/warehouse/inventories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateInventory(id, data) {
    return this.request(`/warehouse/inventories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async completeInventory(id) {
    return this.request(`/warehouse/inventories/${id}/complete`, {
      method: 'POST',
    });
  }

  async deleteInventory(id) {
    return this.request(`/warehouse/inventories/${id}`, {
      method: 'DELETE',
    });
  }

  // === Bouquet Inventories (инвентаризация собранных букетов) ===
  async getBouquetInventories({ limit, offset } = {}) {
    const params = new URLSearchParams();
    if (limit != null) params.append('limit', limit);
    if (offset != null) params.append('offset', offset);
    const query = params.toString();
    return this.request(`/bouquets/inventories${query ? '?' + query : ''}`);
  }

  async getBouquetInventoryCandidates() {
    return this.request('/bouquets/inventories/candidates');
  }

  async getBouquetInventory(id) {
    return this.request(`/bouquets/inventories/${id}`);
  }

  async createBouquetInventory(data) {
    return this.request('/bouquets/inventories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateBouquetInventory(id, data) {
    return this.request(`/bouquets/inventories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async completeBouquetInventory(id) {
    return this.request(`/bouquets/inventories/${id}/complete`, {
      method: 'POST',
    });
  }

  async deleteBouquetInventory(id) {
    return this.request(`/bouquets/inventories/${id}`, {
      method: 'DELETE',
    });
  }

  // === Warehouse: Stock Coverage ===
  async getStockCoverage(days = 21) {
    return this.request(`/warehouse/stock-coverage?days=${days}`);
  }

  // === Warehouse: Movements ===
  async getStockMovements({ productId, type, dateFrom, dateTo, limit, offset } = {}) {
    const params = new URLSearchParams();
    if (productId) params.append('product_id', productId);
    if (type) params.append('type', type);
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (limit != null) params.append('limit', limit);
    if (offset != null) params.append('offset', offset);
    const query = params.toString();
    return this.request(`/warehouse/movements${query ? '?' + query : ''}`);
  }
  // === Subscriptions: Catalog ===
  async getSubscriptionCatalog(all = false) {
    return this.request(`/subscriptions/catalog${all ? '/all' : ''}`);
  }

  async createSubscriptionCatalogItem(data) {
    return this.request('/subscriptions/catalog', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSubscriptionCatalogItem(id, data) {
    return this.request(`/subscriptions/catalog/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSubscriptionCatalogItem(id) {
    return this.request(`/subscriptions/catalog/${id}`, {
      method: 'DELETE',
    });
  }

  async uploadSubscriptionCatalogImage(itemId, file) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_URL}/subscriptions/catalog/${itemId}/images`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${this.token}` },
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Upload failed');
    }
    return response.json();
  }

  async deleteSubscriptionCatalogImage(itemId, imageId) {
    return this.request(`/subscriptions/catalog/${itemId}/images/${imageId}`, {
      method: 'DELETE',
    });
  }

  async rotateSubscriptionCatalogImage(itemId, imageId, direction) {
    return this.request(`/subscriptions/catalog/${itemId}/images/${imageId}/rotate?direction=${direction}`, {
      method: 'POST',
    });
  }

  // === Subscriptions: Discount Tiers ===
  async getSubscriptionDiscountTiers() {
    return this.request('/subscriptions/discount-tiers');
  }

  async updateSubscriptionDiscountTiers(tiers) {
    return this.request('/subscriptions/discount-tiers', {
      method: 'PUT',
      body: JSON.stringify(tiers),
    });
  }

  // === Subscriptions: Management ===
  async getSubscriptions(filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.payment_status) params.append('payment_status', filters.payment_status);
    const query = params.toString();
    return this.request(`/subscriptions${query ? '?' + query : ''}`);
  }

  async getSubscription(id) {
    return this.request(`/subscriptions/${id}`);
  }

  async updateSubscription(id, data) {
    return this.request(`/subscriptions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async fillSubscriptionOrder(subscriptionId, orderId, items) {
    return this.request(`/subscriptions/${subscriptionId}/orders/${orderId}/fill`, {
      method: 'POST',
      body: JSON.stringify({ items }),
    });
  }
  async updateSubscription(subscriptionId, data) {
    return this.request(`/subscriptions/${subscriptionId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async downloadPartnerCard(subscriptionId) {
    const response = await fetch(`${API_URL}/subscriptions/${subscriptionId}/partner-card`, {
      headers: { 'Authorization': `Bearer ${this.token}` },
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      alert(err.detail || 'Файл не найден');
      return;
    }
    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition');
    const match = disposition?.match(/filename\*?=(?:UTF-8''|"?)(.+?)(?:"|;|$)/);
    const filename = match ? decodeURIComponent(match[1]) : 'partner_card';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Certificate config (settings)
  async getCertificateConfig() {
    return this.request('/settings/certificate-config');
  }

  async updateCertificateConfig(payload) {
    return this.request('/settings/certificate-config', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  // Certificates (admin)
  async listCertificates(params = {}) {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.code) qs.set('code', params.code);
    if (params.limit) qs.set('limit', params.limit);
    if (params.offset) qs.set('offset', params.offset);
    return this.request(`/certificates/admin/list?${qs.toString()}`);
  }

  async getCertificate(certId) {
    return this.request(`/certificates/admin/${certId}`);
  }

  async cancelCertificate(certId, reason) {
    return this.request(`/certificates/admin/${certId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async resendCertificate(certId) {
    return this.request(`/certificates/admin/${certId}/resend`, { method: 'POST' });
  }

  async editCertificate(certId, fields) {
    return this.request(`/certificates/admin/${certId}`, {
      method: 'PATCH',
      body: JSON.stringify(fields),
    });
  }

  async getCertificateHistory(certId) {
    return this.request(`/certificates/admin/${certId}/history`);
  }

  // === Flowwow (маркетплейс) ===
  async getFlowwowStatus() {
    return this.request('/flowwow/status');
  }

  async getFlowwowSettings() {
    return this.request('/flowwow/settings');
  }

  // Ключи с префиксом flowwow_ (любое подмножество); ответ — настройки без префикса
  async updateFlowwowSettings(data) {
    return this.request('/flowwow/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getFlowwowOffers() {
    return this.request('/flowwow/offers');
  }

  // Пауза продаж: все карточки скрыты на площадке, заказы продолжают приходить
  // «Не продавать на Flowwow»: kind 'product' | 'bouquet'
  async setFlowwowExcluded(kind, entityId, excluded) {
    return this.request('/flowwow/offers/exclude', {
      method: 'PUT', body: JSON.stringify({ kind, entity_id: entityId, excluded }),
    });
  }

  async setFlowwowTopPinned(kind, entityId, pinned) {
    return this.request('/flowwow/offers/top', {
      method: 'PUT', body: JSON.stringify({ kind, entity_id: entityId, pinned }),
    });
  }

  async setFlowwowPaused(paused) {
    return this.request('/flowwow/pause', { method: 'PUT', body: JSON.stringify({ paused }) });
  }

  async getFlowwowPreview() {
    return this.request('/flowwow/preview');
  }

  async runFlowwowSync(full = false) {
    return this.request(`/flowwow/sync?full=${full ? 'true' : 'false'}`, { method: 'POST' });
  }

  async getFlowwowLog(limit = 100, kind = null) {
    let url = `/flowwow/log?limit=${limit}`;
    if (kind) url += `&kind=${kind}`;
    return this.request(url);
  }

  async pollFlowwowOrders() {
    return this.request('/flowwow/orders/poll', { method: 'POST' });
  }

  async refreshFlowwowRemote() {
    return this.request('/flowwow/refresh-remote', { method: 'POST' });
  }

  async testFlowwowWebhook() {
    return this.request('/flowwow/webhook-test', { method: 'POST' });
  }

  async getFlowwowForeign() {
    return this.request('/flowwow/foreign');
  }

  // Привязать чужие карточки к нашим букетам: [{ product_id, bouquet_id }]
  async linkFlowwowForeign(links) {
    return this.request('/flowwow/foreign/link', { method: 'POST', body: JSON.stringify({ links }) });
  }

  async archiveFlowwowForeign(productIds) {
    return this.request('/flowwow/foreign/archive', { method: 'POST', body: JSON.stringify({ product_ids: productIds }) });
  }

  async getFlowwowOrder(orderId) {
    return this.request(`/flowwow/orders/${orderId}`);
  }

  async acceptFlowwowOrder(orderId) {
    return this.request(`/flowwow/orders/${orderId}/accept`, { method: 'POST' });
  }

  // «Согласовать с получателем» у своей доставки: время вручения у нас
  async flowwowSetWindow(orderId, data) {
    return this.request(`/flowwow/orders/${orderId}/window`, { method: 'POST', body: JSON.stringify(data) });
  }

  // Адрес Flowwow есть в нескольких местах зоны доставки — варианты для выбора в карточке
  async getFlowwowAddressVariants(orderId) {
    return this.request(`/flowwow/orders/${orderId}/address-variants`);
  }

  // «Везём сами» (самодоставка) или «Пересчитать доставку» с поправленным адресом
  async flowwowOwnDelivery(orderId, address) {
    return this.request(`/flowwow/orders/${orderId}/own-delivery`, { method: 'POST', body: JSON.stringify({ address: address || null }) });
  }

  // Ручная отметка оплаты cert (для standalone GiftCertificate после миграции
  // 20260502_0001). Endpoint напрямую на cert.
  async markCertificatePaid(certId, { method, paid_at, comment }) {
    return this.request(`/certificates/admin/${certId}/mark-paid`, {
      method: 'POST',
      body: JSON.stringify({
        method,
        paid_at,
        comment: comment || null,
      }),
    });
  }
}

export const api = new ApiService();
